import { Injectable } from "@angular/core";
import { Functionality } from "../constants/functionality.constants";
import { SessionService } from "./session.service";

/**
 * Answers whether the current session's role grants access to a given functionality, based on
 * the functionalities and routes stored in the session at login.
 *
 * This only drives what the UI shows: the backend remains responsible for rejecting any call
 * the role doesn't grant.
 */
@Injectable({
  providedIn: "root",
})
export class PermissionService {
  constructor(private readonly sessionService: SessionService) {}

  /**
   * Determines whether a functionality is enabled, i.e. granted to the user's role. Actions tied
   * to one specific API route should use isRouteEnabled() instead.
   * @param functionality The functionality to check
   * @return True if the functionality is enabled, false otherwise
   */
  public isEnabled(functionality: Functionality): boolean {
    return (this.sessionService.functionalities ?? []).some((f) => f.name === functionality);
  }

  /**
   * Determines whether an action tied to one specific API route is enabled: both the functionality
   * it belongs to and that route must be granted.
   * @param functionality The functionality the action belongs to
   * @param method HTTP method of the route the action calls (e.g. "POST")
   * @param route Route the action calls, as identified by the backend (e.g. "/upload/rna")
   * @return True if the action is enabled, false otherwise
   */
  public isRouteEnabled(functionality: Functionality, method: string, route: string): boolean {
    return this.isEnabled(functionality) && this.canCall(method, route);
  }

  /**
   * Determines whether an API route is granted.
   * @param method HTTP method of the route (e.g. "POST")
   * @param route Route as identified by the backend (e.g. "/upload")
   * @return True if the route is granted, false otherwise
   */
  public canCall(method: string, route: string): boolean {
    return (this.sessionService.routes ?? []).some((r) => r.method === method && r.route === route);
  }

  /**
   * Determines whether the user can access the administration section: a SuperAdmin to see
   * every organization or create one, or an admin to manage their own organization's members.
   * @return True if the administration section is accessible, false otherwise
   */
  public get canAccessAdministration(): boolean {
    return (
      this.isEnabled(Functionality.VIEW_ORGANIZATIONS) ||
      this.isEnabled(Functionality.CREATE_ORGANIZATION) ||
      this.isEnabled(Functionality.INVITE_MEMBER) ||
      this.isEnabled(Functionality.REVOKE_INVITATION) ||
      this.isEnabled(Functionality.RESEND_INVITATION)
    );
  }
}
